import { Button, Card, Col, Form, Row } from "antd";
import FullPageLayout from "../../../templates/FullPageLayout";
import UploadPhoto from "../../../atoms/UploadPhoto";
import { useEffect, useState } from "react";
import { useForm } from "react-hook-form";
import {
  DateField,
  InputField,
  InputPhone,
  SelectField,
} from "../../../atoms/FormFields";
import { updateUserById } from "../../Team/ducks/services";
import { AxiosError } from "axios";
import { onImageDelete, uploadUserImage } from "../../MyProfile/ducks/services";
import { RcFile } from "antd/es/upload";
import SuccessMessage from "../../../atoms/Toasts/SuccessMessage";
import { useLocation, useNavigate } from "react-router-dom";
import ErrorMessage from "../../../atoms/Toasts/ErrorMessage";
import { useAppDispatch, useAppSelector } from "../../../config/hooks";
import {
  getCompanyFormData,
  getCompanyFormList,
  getUserDetailById,
  removeUserDetailData,
} from "../ducks/actions";
import dayjs from "dayjs";

interface FormValues {
  first_name: string;
  last_name: string;
  first_language: string;
  email: string;
  type: string;
  phone: string;
  phone_office: string;
  joined_date: string | dayjs.Dayjs; // Allow both string and dayjs
  birthday: string | dayjs.Dayjs; // Allow both string and dayjs
  company_id: string;
  position_id: string;
  service_line_id: string[];
  rockstarAt: string;
}

const firstLanguageOptions = [
  { label: "English", value: "english" },
  { label: "Spanish", value: "spanish" },
];
const tempOptions = [
  { label: "Option 1", value: "option 1" },
  { label: "Option 2", value: "option 2" },
];

export default function EditUser() {
  const {
    control,
    handleSubmit,
    setValue,
    formState: { errors },
  } = useForm<FormValues>();

  const navigate = useNavigate();
  const dispatch = useAppDispatch();
  const url = useLocation();
  const id = url?.pathname.split("/")[2];

  const [loading, setLoading] = useState(false);
  const [checklistFile, setChecklistFile] = useState<File | null>(null);
  const [uploadModal, setUploadModal] = useState<boolean>(false);
  const companyList = useAppSelector((state) => state?.users?.companyFormList);
  const userDetailData = useAppSelector(
    (state) => state?.users?.userDetailData
  );
  const companyFormData = useAppSelector(
    (state) => state?.users?.companyFormData
  );
  console.log(companyFormData);
  const [previewImage, setPreviewImage] = useState<{
    file: string;
    imagePreviewUrl: string | null;
  }>({ file: "", imagePreviewUrl: null });
  const [modalPreviewImage, setModalPreviewImage] = useState<{
    file: string;
    imagePreviewUrl: string | null;
  }>({ file: "", imagePreviewUrl: null });

  const saveUpload = () => {
    setLoading(true);
    setPreviewImage({ ...modalPreviewImage });
    setLoading(false);
    setUploadModal(false);
  };

  const onSubmit = (val: FormValues) => {
    setLoading(true);
    const payload = {
      first_name: val.first_name,
      last_name: val.last_name,
      email: val.email,
      type: "client",
      phone: val?.phone ? val?.phone : undefined,
      position_id: val.position_id,
      service_line_id: val.service_line_id,
      rockstar_at: val.rockstarAt,
      first_language: val.first_language,
      joined_date: val?.joined_date
        ? dayjs(val.joined_date).toJSON()
        : undefined,
      birthday: val?.birthday ? dayjs(val.birthday).toJSON() : undefined,
      company_id: val.company_id,
    };

    updateUserById(id, payload)
      .then(() => {
        if (checklistFile && id) {
          uploadUserImage({
            owner_id: id,
            image: checklistFile as File | RcFile,
            image_type: "user_image",
          })
            .then((res) => {
              if (res) {
                setLoading(false);
                SuccessMessage("User updated");
                navigate("/customer-users");
              }
            })
            .catch((e: unknown) => {
              if (e instanceof AxiosError) {
                setLoading(false);
                ErrorMessage(
                  e?.response?.data?.error?.message ?? "Something went wrong"
                );
              } else {
                setLoading(false);
                ErrorMessage("Something went wrong");
              }
            });
        } else {
          setLoading(false);
          SuccessMessage("User updated");
          navigate("/customer-users");
        }
      })
      .catch((e: unknown) => {
        if (e instanceof AxiosError) {
          setLoading(false);
          ErrorMessage(
            e?.response?.data?.error?.message ?? "Something went wrong"
          );
        } else {
          setLoading(false);
          ErrorMessage("Something went wrong");
        }
      });
  };

  useEffect(() => {
    dispatch(getCompanyFormList());
  }, [dispatch]);

  useEffect(() => {
    if (id) {
      dispatch(getUserDetailById(id));
    }
  }, [id, dispatch]);

  const onChange = (value: string | number | readonly string[]) => {
    dispatch(getCompanyFormData(value as string));
  };

  useEffect(() => {
    if (userDetailData?.data) {
      if (userDetailData?.data?.company_id) {
        dispatch(
          getCompanyFormData(userDetailData?.data?.company_id as string)
        );
      }

      setValue("first_name", userDetailData?.data?.first_name);
      setValue("last_name", userDetailData?.data?.last_name);
      setValue("email", userDetailData?.data?.email);
      setValue("first_language", userDetailData?.data?.first_language);
      setValue("company_id", userDetailData?.data?.company_id);
      setValue("position_id", userDetailData?.data?.position_id);
      setValue("service_line_id", userDetailData?.data?.service_line_id);
      setValue("rockstarAt", userDetailData?.data?.rockstar_at);
      if (userDetailData?.data?.phone) {
        setValue("phone", userDetailData?.data?.phone);
      }
      if (userDetailData?.data?.birthday) {
        const tempBirthday = dayjs(userDetailData.data.birthday);

        if (tempBirthday.isValid()) {
          setValue("birthday", tempBirthday); // Pass a Day.js object, NOT a string
        }
      }
      if (userDetailData?.data?.joined_date) {
        const tempJoinedDate = dayjs(userDetailData.data.joined_date);

        if (tempJoinedDate.isValid()) {
          setValue("joined_date", tempJoinedDate); // Pass a Day.js object, NOT a string
        }
      }
    }
  }, [userDetailData, setValue, dispatch]);

  function callMembersAPI() {
    dispatch(getUserDetailById(id));
  }

  useEffect(() => {
    dispatch(removeUserDetailData());
  }, [dispatch]);

  return (
    <FullPageLayout loading={userDetailData?.loader || loading}>
      <Form
        layout="vertical"
        onFinish={handleSubmit(onSubmit)}
        onKeyDown={(e) => {
          if (e.key === "Enter") e.preventDefault();
        }}
      >
        <Col className="addTeamMember">
          <div className="title">
            <h2> Edit user </h2>
            <p>Complete the form below to add a user to a company.</p>
          </div>
          <Card>
            <Row gutter={20}>
              <Col>
                <h4 style={{ marginBottom: "16px" }}> Job details </h4>
              </Col>
              <Col span={24}>
                <SelectField
                  fieldName="company_id"
                  label="Company"
                  control={control}
                  selectOption={companyList}
                  iProps={{ placeholder: "Select company" }}
                  onSelectChange={onChange}
                  isRequired
                  rules={{
                    required: "Company is required.",
                  }}
                  validate={errors.company_id && "error"}
                  validMessage={errors.company_id && errors.company_id.message}
                />
              </Col>
            </Row>
            {companyFormData?.position?.length > 0 && (
              <Row gutter={20}>
                <Col span={24}>
                  <SelectField
                    fieldName="position_id"
                    label="Position"
                    control={control}
                    selectOption={companyFormData?.position}
                    iProps={{ placeholder: "Select position" }}
                    isRequired
                    rules={{
                      required: "Position is required.",
                    }}
                    validate={errors.position_id && "error"}
                    validMessage={
                      errors.position_id && errors.position_id.message
                    }
                  />
                </Col>
                <Col span={24}>
                  <SelectField
                    fieldName="service_line_id"
                    label="Service lines"
                    control={control}
                    selectOption={companyFormData?.service_line}
                    iProps={{
                      placeholder: "Select service lines",
                      mode: "multiple",
                    }}
                    tooltipTitle="Service lines managed by this employee."
                  />
                </Col>
                <Col span={24} className="mb-0">
                  <SelectField
                    fieldName="rockstarAt"
                    label="Rockstar at"
                    control={control}
                    selectOption={tempOptions}
                    iProps={{
                      placeholder: "Select rockstar at",
                      // mode: "multiple",
                    }}
                  />
                </Col>
              </Row>
            )}
          </Card>

          <Card>
            <Row gutter={20}>
              <Col span={24} style={{ marginBottom: "20px" }}>
                <h4 style={{ marginBottom: "16px" }}> Personal details </h4>
                <UploadPhoto
                  saveUpload={saveUpload}
                  loading={loading}
                  uploadModal={uploadModal}
                  imageUploaded={userDetailData?.data?.image_url}
                  setUploadModal={setUploadModal}
                  onImageDelete={() => {
                    onImageDelete(
                      setLoading,
                      SuccessMessage,
                      callMembersAPI,
                      ErrorMessage,
                      userDetailData?.data?.image_url
                        ? userDetailData?.data?.image_id
                        : undefined
                    );
                  }}
                  previewImage={previewImage}
                  checklistFile={checklistFile}
                  modalPreviewImage={modalPreviewImage}
                  setChecklistFile={setChecklistFile}
                  setPreviewImage={setPreviewImage}
                  setModalPreviewImage={setModalPreviewImage}
                  infoTextUploader="A photo helps others recognize this employee easily. It’s optional."
                  //   setIsCropModalOpen={setIsCropModalOpen}
                />
              </Col>

              <Col span={12}>
                <InputField
                  fieldName="first_name"
                  label="First name"
                  control={control}
                  defaultValue=""
                  iProps={{
                    placeholder: "Enter first name",
                    maxLength: 100,
                  }}
                  isRequired
                  rules={{
                    required: "First name is required.",
                  }}
                  validate={errors.first_name && "error"}
                  validMessage={errors.first_name && errors.first_name.message}
                />
              </Col>
              <Col span={12}>
                <InputField
                  fieldName="last_name"
                  label="Last name"
                  control={control}
                  defaultValue=""
                  iProps={{
                    placeholder: "Enter last name",
                    maxLength: 100,
                  }}
                  isRequired
                  rules={{
                    required: "Last name is required.",
                  }}
                  validate={errors.last_name && "error"}
                  validMessage={errors.last_name && errors.last_name.message}
                />
              </Col>
              <Col span={24}>
                <SelectField
                  fieldName="first_language"
                  label="First language"
                  control={control}
                  selectOption={firstLanguageOptions}
                  iProps={{ placeholder: "Select language" }}
                  isRequired
                  defaultValue="english"
                  rules={{
                    required: "First language is required.",
                  }}
                  validate={errors.first_language && "error"}
                  validMessage={
                    errors.first_language && errors.first_language.message
                  }
                />
              </Col>
              <Col span={12}>
                <InputField
                  fieldName="email"
                  label="Account email"
                  control={control}
                  defaultValue=""
                  iProps={{
                    placeholder: "Enter account email",
                    autoComplete: "account_email",
                    maxLength: 100,
                  }}
                  isRequired
                  rules={{
                    required: "Account email is required.",
                    pattern: {
                      value: /^[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}$/,
                      message: "Please enter a valid email address.",
                    },
                  }}
                  validate={errors.email && "error"}
                  validMessage={errors.email && errors.email.message}
                />
              </Col>
              <Col span={12}>
                <InputPhone fieldName="phone" label="Phone" control={control} />
              </Col>
              <Col span={12} className="mb-0">
                <DateField
                  fieldName="joined_date"
                  label="Joined company"
                  control={control}
                />
              </Col>
              <Col span={12} className="mb-0">
                <DateField
                  fieldName="birthday"
                  label="Birthdate"
                  control={control}
                />
              </Col>
            </Row>
          </Card>
        </Col>
        <div className="fixed_btm_card">
          <Card>
            <div className="centered">
              <Button
                htmlType="button"
                className="outlined-btn"
                onClick={() => navigate("/customer-users")}
              >
                Cancel
              </Button>
              <div style={{ display: "flex", gap: "16px" }}>
                {/* <Button
                  htmlType="submit"
                  className="outlined-btn"
                  type="primary"
                  loading={loading}
                >
                  Save & add one more
                </Button> */}
                <Button htmlType="submit" type="primary" className="mint-btn">
                  Save & close
                </Button>
              </div>
            </div>
          </Card>
        </div>
      </Form>
    </FullPageLayout>
  );
}
